import React, { memo, useContext, useEffect } from 'react'
import PropTypes from 'prop-types'
import { DatePicker } from '@mui/x-date-pickers'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import { ThemeProvider, createTheme } from '@mui/material/styles'
import moment from 'moment'
import { TextField } from '@mui/material'
import 'moment/min/locales'

import { LanguageContext } from '../../../context/LanguageContext'
import { util } from '../../../util'

/**
 *
 * @param selectedDate {String} -> The date that is selected or null.
 * @param handleDateChange {Callback} -> Callback function that triggers on change of date
 * @param name {String} -> Uniquely identified name of date picker
 * @param label {String} -> Uniquely identified label of date picker
 * @param error {String} -> Custom error message that is displayed below date picker
 * @param size {String} -> Size of date picker
 * @returns {*}
 * @constructor
 */

const CustomDatePicker = ({
    className,
    error,
    format,
    handleDateChange,
    isFullWidth,
    label,
    name,
    selectedDate,
    size,
    disabled = false,
    ...rest
}) => {
    // Context
    const { strings, language } = useContext(LanguageContext)

    //useEffect functions
    useEffect(() => {
        moment.locale(language)
    }, [language])

    //Theme

    const theme = createTheme({
        components: {
            MuiInputLabel: {
                styleOverrides: {
                    root: {
                        fontSize: '4rem',
                        color: '#5B616B',
                        fontWeight: 'bold',
                        transform: 'translate(14px, 12px) scale(1)',
                        '&.MuiInputLabel-shrink': {
                            transform: 'translate(14px, -9px) scale(0.75)',
                        },
                    },
                },
            },
            MuiOutlinedInput: {
                styleOverrides: {
                    root: {
                        fontSize: '2rem',
                    },
                    input: {
                        padding: '14px',
                    },
                },
            },
            MuiPickersDay: {
                styleOverrides: {
                    root: {
                        fontSize: '3rem',
                    },
                },
            },
            MuiTypography: {
                styleOverrides: {
                    root: {
                        fontSize: '3rem',
                    },
                    h4: {
                        fontSize: '3rem',
                    },
                    subtitle1: {
                        fontSize: '3rem',
                    },
                },
            },
            MuiIconButton: {
                styleOverrides: {
                    root: {
                        fontSize: '3rem',
                    },
                },
            },
            MuiSvgIcon: {
                styleOverrides: {
                    root: {
                        fontSize: '5rem',
                    },
                },
            },
            MuiTextField: {
                styleOverrides: {
                    root: {
                        fontSize: '1.6rem',
                    },
                },
            },
            MuiPickersToolbar: {
                styleOverrides: {
                    root: {
                        fontSize: '1.6rem',
                    },
                },
            },
            MuiPickersCalendarHeader: {
                styleOverrides: {
                    switchHeader: {
                        fontSize: '3.6rem',
                    },
                    label: {
                        fontSize: '3.5rem',
                    },
                },
            },
            MuiPickersCalendar: {
                styleOverrides: {
                    transitionContainer: {
                        fontSize: '1.6rem',
                    },
                },
            },
        },
    })

    const textFieldStyles = {
        mt: '1px',
        '& .MuiOutlinedInput-root': {
            fontSize: '4.35rem',
            height: '14rem',
            '& input': {
                padding: '8px 10px',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#d5d5d7',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#d5d5d7',
                borderWidth: '0.25rem',
            },
        },
        '& .MuiInputLabel-root': {
            color: 'black',
        },
        '& .MuiInputLabel-root.Mui-focused': {
            color: 'black',
        },
    }

    // Convert selectedDate (string) to moment object
    const convertedSelectedDate = selectedDate ? moment(selectedDate, format) : null

    // Handle date change, format to the desired format and pass back to the parent
    const handleDateChangeInternal = date => {
        if (date) {
            // Convert date to a string with the provided format
            const formattedDate = date.format(format || 'MM/DD/YYYY')
            handleDateChange(formattedDate, name)
        }
    }

    return (
        <ThemeProvider theme={theme}>
            <LocalizationProvider
                libInstance={moment}
                dateAdapter={AdapterMoment}
                adapterLocale={language}
            >
                <div
                    className={util.joinClasses(
                        'picker',
                        size,
                        isFullWidth && 'is-full-width',
                        className
                    )}
                >
                    <DatePicker
                        label={label}
                        format={format || 'MM/DD/YYYY'}
                        value={convertedSelectedDate} // Pass moment object
                        onChange={handleDateChangeInternal} // Use internal handler
                        disabled={disabled}
                        renderInput={params => (
                            <TextField
                                {...params}
                                placeholder={strings.SELECT_DATE}
                                fullWidth
                                variant="outlined"
                                margin="normal"
                                sx={textFieldStyles}
                            />
                        )}
                        {...rest}
                    />
                    {!!error && <span className={'picker__error'}>{error}</span>}
                </div>
            </LocalizationProvider>
        </ThemeProvider>
    )
}

CustomDatePicker.propTypes = {
    selectedDate: PropTypes.string,
    handleDateChange: PropTypes.func,
    name: PropTypes.string,
    label: PropTypes.string,
    error: PropTypes.string,
    size: PropTypes.string,
}

export default memo(CustomDatePicker)
